import { Assets, Texture } from 'pixi.js';
import type { Manifest } from '../interfaces/IAssetManager';
import { Spritesheet } from 'pixi.js';

// Legacy manifest loading (for backward compatibility)
export async function loadManifest(
  baseUrl: string,
  slug: string
): Promise<Manifest> {
  const url = `${baseUrl}/games/${slug}/manifest.json`;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    return await res.json();
  } catch (error) {
    console.warn(`[AssetLoader] Failed to load manifest for ${slug}:`, error);
    return {
      version: '1.0.0',
      packs: { main: { images: [], sounds: [], atlases: [], spine: [] } },
    };
  }
}

export async function loadAtlas(
  baseUrl: string,
  slug: string,
  jsonPath: string,
  imagePath: string
): Promise<Spritesheet> {
  const base = `${baseUrl}/games/${slug}`;
  const texture = await Assets.load(`${base}/${imagePath}`);
  const data = await fetch(`${base}/${jsonPath}`).then(r => r.json());

  const sheet = new Spritesheet(texture, data);
  await sheet.parse();
  return sheet;
}

// Legacy pack loading (for backward compatibility)
export async function loadPack(
  baseUrl: string,
  slug: string,
  manifest: Manifest,
  pack: string
): Promise<void> {
  const p = manifest.packs[pack];
  if (!p) return;

  const base = `${baseUrl}/games/${slug}`;
  const promises: Promise<any>[] = [];

  // Load visual assets
  const visualUrls: string[] = [
    ...(p.images?.map(x => `${base}/${x}`) || []),
    ...(p.atlases?.map(x => `${base}/${x}`) || []),
    ...(p.spine?.map(x => `${base}/${x}`) || []),
  ];

  const validVisualUrls = visualUrls.filter(
    url => typeof url === 'string' && !url.endsWith('[object Object]')
  );

  if (validVisualUrls.length) {
    const visualPromises = validVisualUrls.map(url =>
      Assets.load(url).catch(error => {
        console.warn(
          `[AssetLoader] Failed to load visual asset ${url}:`,
          error
        );
        return null;
      })
    );
    promises.push(Promise.all(visualPromises));
  }

  await Promise.allSettled(promises);
}

// Legacy texture getter (for backward compatibility)
export function getTexture(
  baseUrl: string,
  slug: string,
  path: string
): Texture | null {
  const fullPath = `${baseUrl}/games/${slug}/${path}`;
  const asset = Assets.cache.get(fullPath);
  return asset instanceof Texture ? asset : null;
}

// Legacy asset loader (for backward compatibility)
export async function loadAsset(
  baseUrl: string,
  slug: string,
  path: string
): Promise<any> {
  const fullPath = `${baseUrl}/games/${slug}/${path}`;
  return Assets.load(fullPath);
}

// Legacy spine resource getter (for backward compatibility)
export function getSpineResource(
  baseUrl: string,
  slug: string,
  path: string
): any {
  const fullPath = `${baseUrl}/games/${slug}/${path}`;
  return Assets.cache.get(fullPath) || null;
}

// Legacy audio getter (for backward compatibility)
export function getAudio(
  _baseUrl: string,
  _slug: string,
  _path: string
): HTMLAudioElement | undefined {
  // This is a placeholder - audio elements aren't cached in PIXI Assets
  // You might want to implement a separate audio cache
  return undefined;
}

// Legacy spine loader (for backward compatibility)
export async function loadSpine(
  baseUrl: string,
  slug: string,
  jsonPath: string
): Promise<any> {
  const fullPath = `${baseUrl}/games/${slug}/${jsonPath}`;
  return Assets.load(fullPath);
}
